function normalizeComparableValue(value) {
  if (value == null || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : String(value);
}

function aggregateDisplayValue(values) {
  const presentValues = values.filter((value) => value != null && value !== '');
  if (!presentValues.length) return null;

  const normalizedValues = presentValues.map(normalizeComparableValue);
  const uniqueValues = Array.from(new Set(normalizedValues));
  if (uniqueValues.length === 1) return uniqueValues[0];

  if (uniqueValues.every((value) => typeof value === 'number')) {
    return `${Math.min(...uniqueValues)}–${Math.max(...uniqueValues)}`;
  }
  return 'Varies';
}

function suggestionGroupKey(suggestion) {
  return JSON.stringify([
    suggestion.element,
    normalizeComparableValue(suggestion.suggested_threshold)
  ]);
}

function getSuggestionOrigin(value) {
  try {
    return new URL(value).origin;
  } catch (_) {
    return value || 'Unknown origin';
  }
}

export function groupThresholdSuggestionsByOrigin(urls) {
  const groupsByOrigin = new Map();

  (Array.isArray(urls) ? urls : []).forEach((url) => {
    const suggestions = Array.isArray(url.suggestions) ? url.suggestions : [];
    if (!suggestions.length) return;

    const origin = getSuggestionOrigin(url.url);
    if (!groupsByOrigin.has(origin)) {
      groupsByOrigin.set(origin, {
        groupKey: origin,
        origin,
        urlIds: new Set(),
        incidentCount: 0,
        suggestionsByGroupKey: new Map()
      });
    }

    const group = groupsByOrigin.get(origin);
    const urlId = Number(url.urlID);
    if (!group.urlIds.has(urlId)) {
      group.urlIds.add(urlId);
      group.incidentCount += Number(url.incident_count) || 0;
    }

    suggestions.forEach((suggestion) => {
      const element = suggestion.element;
      const groupKey = suggestionGroupKey(suggestion);
      if (!group.suggestionsByGroupKey.has(groupKey)) {
        group.suggestionsByGroupKey.set(groupKey, {
          element,
          rowKey: `${origin}::${groupKey}`,
          targetUrlIds: new Set(),
          affectedUrls: [],
          baselineValues: [],
          currentThresholdValues: [],
          suggestedThresholdValues: [],
          modeDifferenceValues: [],
          sampleCount: 0,
          incidentCount: 0,
          hasSampleCount: false,
          hasIncidentCount: false
        });
      }

      const groupedSuggestion = group.suggestionsByGroupKey.get(groupKey);
      if (!groupedSuggestion.targetUrlIds.has(urlId)) {
        groupedSuggestion.targetUrlIds.add(urlId);
        groupedSuggestion.affectedUrls.push({
          urlId,
          url: url.url,
          baselineValue: suggestion.baseline_value,
          currentThreshold: suggestion.current_threshold,
          suggestedThreshold: suggestion.suggested_threshold
        });
        groupedSuggestion.baselineValues.push(suggestion.baseline_value);
        groupedSuggestion.currentThresholdValues.push(
          suggestion.current_threshold
        );
        groupedSuggestion.suggestedThresholdValues.push(
          suggestion.suggested_threshold
        );
        groupedSuggestion.modeDifferenceValues.push(suggestion.mode_difference);
        if (suggestion.sample_count != null) {
          groupedSuggestion.sampleCount += Number(suggestion.sample_count) || 0;
          groupedSuggestion.hasSampleCount = true;
        }
        if (url.incident_count != null) {
          groupedSuggestion.incidentCount += Number(url.incident_count) || 0;
          groupedSuggestion.hasIncidentCount = true;
        }
      }
    });
  });

  return Array.from(groupsByOrigin.values()).map((group) => {
    const suggestions = Array.from(group.suggestionsByGroupKey.values()).map(
      (suggestion) => {
        const {
          targetUrlIds,
          affectedUrls,
          baselineValues,
          currentThresholdValues,
          suggestedThresholdValues,
          modeDifferenceValues,
          sampleCount,
          incidentCount,
          hasSampleCount,
          hasIncidentCount,
          ...suggestionValues
        } = suggestion;
        return {
          ...suggestionValues,
          targetUrlIds: Array.from(targetUrlIds),
          affectedUrls,
          baseline_value: aggregateDisplayValue(baselineValues),
          current_threshold: aggregateDisplayValue(currentThresholdValues),
          suggested_threshold: aggregateDisplayValue(suggestedThresholdValues),
          mode_difference: aggregateDisplayValue(modeDifferenceValues),
          sampleCount: hasSampleCount ? sampleCount : null,
          incidentCount: hasIncidentCount ? incidentCount : null
        };
      }
    );

    return {
      groupKey: group.groupKey,
      origin: group.origin,
      urlIds: Array.from(group.urlIds),
      incidentCount: group.incidentCount,
      suggestions
    };
  });
}
